import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Sector } from "recharts";

interface PieData {
  name: string;
  value: number;
  color: string;
}

interface CustomPieChartProps {
  data: PieData[];
  height?: string;
  legend?: boolean;
}

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  } = props;

  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 8}
        y={ey}
        textAnchor={textAnchor}
        fill="#333"
        fontSize={14}
      >
        {payload.name}: {value}
      </text>
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 8}
        y={ey + 20}
        textAnchor={textAnchor}
        fill="#999"
        fontSize={13}
      >
        ({(percent * 100).toFixed(2)}%)
      </text>
    </g>
  );
};

const CustomPieChart: React.FC<CustomPieChartProps> = ({
  data,
  height = "h-[28rem]",
  legend = true,
}) => {
  const [activeIndex, setActiveIndex] = React.useState(0);

  // Handle empty or invalid data
  const safeData = React.useMemo(() => {
    // Filter out any invalid entries (null, undefined, missing properties)
    return Array.isArray(data)
      ? data.filter(
        (item) =>
          item &&
          typeof item === "object" &&
          "name" in item &&
          "value" in item &&
          "color" in item &&
          item.value > 0
      )
      : [];
  }, [data]);

  const total = safeData.reduce((sum, item) => sum + (item?.value || 0), 0);

  // If no valid data, show a placeholder
  if (safeData.length === 0) {
    return (
      <div
        className={`w-full flex flex-col items-center justify-center ${height} text-gray-400`}
      >
        <div className="text-center">
          <svg
            className="w-16 h-16 mx-auto mb-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
              clipRule="evenodd"
            />
          </svg>
          <p>No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full flex flex-col items-center ${height}`}>
      <div className="flex-1 flex items-center justify-center relative w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={safeData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={120}
              paddingAngle={5}
              dataKey="value"
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(-1)}
            >
              {safeData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry?.color || "#ccc"} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {/* Centered total */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-800">{total}</div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
        </div>
      </div>
      {legend && (
        <div className="flex justify-center gap-6 mt-4 flex-wrap">
          {data.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item.color }}
              ></div>
              <span className="text-sm text-gray-600">{item.name}</span>
              <span className="text-sm font-medium text-gray-800">
                {item.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomPieChart;
